/**
 * Ops simulation T16 — the owner sets, resets or removes a staff member's POS
 * PIN. A manager's or owner's PIN approves protected actions on a cashier's
 * till; setting it here also lifts a 15-minute lockout.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, useToast } from '@bbpos/ui';
import { ApiError } from '@/lib/api-client';
import { posPinService } from '@/services/pos-pin.service';

const PIN_RE = /^\d{4,6}$/;

export function UserPosPinCard({ userId, userName }: { userId: string; userName: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['user', userId, 'pos-pin'],
    queryFn: () => posPinService.getStatus(userId),
  });
  const hasPin = data?.has_pin;

  const onDone = (message: string) => {
    setPin('');
    setError(null);
    queryClient.invalidateQueries({ queryKey: ['user', userId, 'pos-pin'] });
    toast({ title: message, variant: 'success' });
  };
  const onFail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Could not update the PIN.');

  const setMutation = useMutation({
    mutationFn: () => posPinService.set(userId, pin),
    onSuccess: () => onDone(`POS PIN saved for ${userName}`),
    onError: onFail,
  });
  const clearMutation = useMutation({
    mutationFn: () => posPinService.clear(userId),
    onSuccess: () => onDone(`POS PIN removed for ${userName}`),
    onError: onFail,
  });

  const save = () => {
    if (!PIN_RE.test(pin)) {
      setError('The PIN must be 4 to 6 digits.');
      return;
    }
    setMutation.mutate();
  };

  return (
    <div
      className="rounded-2xl border border-slate-200/80 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-md hover:shadow-lg transition-all duration-200 p-5 mb-6"
      data-testid="user-pos-pin-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white">
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">POS PIN</h3>
              {hasPin !== undefined && (
                <span
                  className={`text-xs font-medium rounded-full px-2 py-0.5 ${
                    hasPin
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  }`}
                >
                  {hasPin ? 'Set' : 'Not set'}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 dark:text-neutral-400">
              Managers and owners approve returns and other actions on a till with this PIN.
            </p>
            {error && <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{error}</p>}
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div className="w-36">
            <Input
              label={hasPin ? 'New PIN' : 'PIN'}
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(v) => {
                setError(null);
                setPin(v.replace(/\D/g, '').slice(0, 6));
              }}
            />
          </div>
          <Button variant="primary" size="sm" loading={setMutation.isPending} onClick={save}>
            {hasPin ? 'Reset PIN' : 'Set PIN'}
          </Button>
          {hasPin && (
            <Button
              variant="ghost"
              size="sm"
              loading={clearMutation.isPending}
              onClick={() => clearMutation.mutate()}
            >
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
